import { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../contexts/AuthContext";
import {
  Newspaper,
  RefreshCcw,
  Loader2,
  Check,
  X,
  Trash2,
  Star,
  Edit3,
  ExternalLink,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Rss,
} from "lucide-react";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "published", label: "Published" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

function badge(status) {
  const map = {
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    published: "bg-emerald-100 text-emerald-700 border-emerald-200",
    rejected: "bg-red-100 text-red-700 border-red-200",
  };
  return map[status] || "bg-gray-100 text-gray-600 border-gray-200";
}

export default function NewsManager() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [stats, setStats] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ url: "", title: "", snippet: "", source: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", snippet: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit: 25 };
      if (tab !== "all") params.status = tab;
      if (search) params.search = search;
      const [{ data }, statsRes] = await Promise.all([
        adminApi.get("/admin/news", { params }),
        adminApi.get("/admin/news/stats"),
      ]);
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setStats(statsRes.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab, page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(""), 2500);
  };

  const patch = async (id, payload) => {
    try {
      const { data } = await adminApi.patch(`/admin/news/${id}`, payload);
      setRows((prev) => prev.map((r) => (r.id === id ? data.item : r)));
      flash("Updated");
      // Refresh stats
      const s = await adminApi.get("/admin/news/stats");
      setStats(s.data);
    } catch (e) {
      alert("Failed: " + formatApiError(e));
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title.slice(0, 60)}…"?`)) return;
    try {
      await adminApi.delete(`/admin/news/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotal((t) => Math.max(0, t - 1));
      flash("Deleted");
      const s = await adminApi.get("/admin/news/stats");
      setStats(s.data);
    } catch (e) {
      alert("Failed: " + formatApiError(e));
    }
  };

  const handleFetchNow = async () => {
    setFetching(true);
    setError("");
    try {
      const { data } = await adminApi.post("/admin/news/fetch");
      const s = data.summary || {};
      flash(
        `Fetched ${s.total_seen || 0} · Matched ${s.total_matched || 0} · New ${s.new_inserted || 0}`
      );
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setFetching(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualForm.url.trim()) return;
    try {
      const { data } = await adminApi.post("/admin/news/manual", manualForm);
      flash(data.item.duplicate ? "Already exists" : "Added");
      setManualForm({ url: "", title: "", snippet: "", source: "" });
      setManualOpen(false);
      await load();
    } catch (e) {
      alert("Failed: " + formatApiError(e));
    }
  };

  const startEdit = (row) => {
    setEditing(row.id);
    setEditForm({ title: row.title, snippet: row.snippet || "" });
  };

  const saveEdit = async () => {
    if (!editing) return;
    await patch(editing, { title: editForm.title, snippet: editForm.snippet });
    setEditing(null);
  };

  return (
    <div className="space-y-5" data-testid="admin-news-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Eczar, serif" }}>
            News Manager
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Curate news items fetched from trusted sources. Only <strong>Published</strong> items appear on the public /news page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setManualOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
            data-testid="add-manual-btn"
          >
            <Plus className="w-4 h-4" /> Add URL
          </button>
          <button
            onClick={handleFetchNow}
            disabled={fetching}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[#FF9933] hover:bg-[#E67300] text-white rounded-md transition-colors font-medium disabled:opacity-60"
            data-testid="fetch-now-btn"
          >
            {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rss className="w-4 h-4" />}
            Fetch Now
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="news-stats">
          {[
            { label: "Pending", value: stats.pending, color: "text-amber-600" },
            { label: "Published", value: stats.published, color: "text-emerald-600" },
            { label: "Rejected", value: stats.rejected, color: "text-red-600" },
            { label: "Featured", value: stats.featured, color: "text-[#FF9933]" },
            { label: "Total", value: stats.total, color: "text-gray-700" },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-white border border-gray-200 rounded-lg px-4 py-3"
            >
              <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {stats?.last_fetched && (
        <p className="text-xs text-gray-400" data-testid="last-fetched">
          Last fetch: {new Date(stats.last_fetched).toLocaleString()} · Next auto-fetch: daily 06:00 IST
        </p>
      )}

      {actionMsg && (
        <div
          className="px-4 py-2.5 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm flex items-center gap-2"
          data-testid="action-message"
        >
          <Check className="w-4 h-4" /> {actionMsg}
        </div>
      )}
      {error && (
        <div className="px-4 py-2.5 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg p-3">
        <div className="flex flex-wrap gap-1" data-testid="news-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setPage(1);
              }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                tab === t.key
                  ? "bg-[#FF9933] text-white font-medium"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`news-tab-${t.key}`}
            >
              {t.label}
              {tab === t.key && stats && (
                <span className="ml-1 text-white/80">
                  ({t.key === "all" ? stats.total : stats[t.key]})
                </span>
              )}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(searchInput.trim());
          }}
          className="relative"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search title, source…"
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none w-64"
            data-testid="news-search"
          />
        </form>
      </div>

      {/* List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 text-[#FF9933] animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm" data-testid="news-empty">
            No news items in this category yet.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((r, i) => (
              <li
                key={r.id}
                className="p-4 hover:bg-gray-50 transition-colors"
                data-testid={`news-row-${i}`}
              >
                {editing === r.id ? (
                  <div className="space-y-2">
                    <input
                      value={editForm.title}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, title: e.target.value }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
                      data-testid={`edit-title-${i}`}
                    />
                    <textarea
                      value={editForm.snippet}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, snippet: e.target.value }))
                      }
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none resize-none"
                      data-testid={`edit-snippet-${i}`}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={saveEdit}
                        className="px-3 py-1.5 text-xs bg-[#FF9933] hover:bg-[#E67300] text-white rounded-md font-medium"
                        data-testid={`save-edit-${i}`}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditing(null)}
                        className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                      >
                        Cancel
                      </button>
                      <span className="text-[11px] text-amber-600 ml-2">
                        Editing invalidates cached translations.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-medium border ${badge(
                            r.status
                          )} uppercase`}
                        >
                          {r.status}
                        </span>
                        <span className="text-[11px] text-gray-500">{r.source}</span>
                        <span className="text-[11px] text-gray-400">
                          {r.published_at ? new Date(r.published_at).toLocaleDateString() : ""}
                        </span>
                        {r.featured && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-[#FF9933]">
                            <Star className="w-3 h-3" fill="currentColor" /> Featured
                          </span>
                        )}
                      </div>
                      <h4 className="font-semibold text-gray-900 text-sm leading-snug mb-1">
                        {r.title}
                      </h4>
                      <p className="text-xs text-gray-500 line-clamp-2 mb-2">{r.snippet}</p>
                      <a
                        href={r.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#FF9933] hover:underline"
                      >
                        Read on source <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="flex flex-wrap items-center gap-1 flex-shrink-0">
                      {r.status !== "published" && (
                        <button
                          onClick={() => patch(r.id, { status: "published" })}
                          title="Publish"
                          className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50"
                          data-testid={`publish-btn-${i}`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      {r.status === "published" && (
                        <button
                          onClick={() => patch(r.id, { status: "pending" })}
                          title="Unpublish"
                          className="p-1.5 rounded text-gray-600 hover:bg-gray-100"
                          data-testid={`unpublish-btn-${i}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      {r.status !== "rejected" && (
                        <button
                          onClick={() => patch(r.id, { status: "rejected" })}
                          title="Reject"
                          className="p-1.5 rounded text-red-500 hover:bg-red-50"
                          data-testid={`reject-btn-${i}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => patch(r.id, { featured: !r.featured })}
                        title={r.featured ? "Unfeature" : "Feature (pin)"}
                        className={`p-1.5 rounded ${
                          r.featured
                            ? "text-[#FF9933] hover:bg-orange-50"
                            : "text-gray-500 hover:bg-gray-100"
                        }`}
                        data-testid={`feature-btn-${i}`}
                      >
                        <Star className="w-4 h-4" fill={r.featured ? "currentColor" : "none"} />
                      </button>
                      <button
                        onClick={() => startEdit(r)}
                        title="Edit"
                        className="p-1.5 rounded text-gray-600 hover:bg-gray-100"
                        data-testid={`edit-btn-${i}`}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(r)}
                        title="Delete"
                        className="p-1.5 rounded text-red-500 hover:bg-red-50"
                        data-testid={`delete-btn-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <p className="text-xs text-gray-500">
              Page {page} of {pages} · {total} total
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 text-gray-600 hover:text-[#FF9933] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
                className="p-1.5 text-gray-600 hover:text-[#FF9933] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Manual add modal */}
      {manualOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setManualOpen(false)}
          data-testid="manual-modal"
        >
          <div
            className="bg-white rounded-lg max-w-lg w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add News Manually</h3>
              <button
                onClick={() => setManualOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleManualSubmit} className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                  Article URL <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  type="url"
                  value={manualForm.url}
                  onChange={(e) =>
                    setManualForm((f) => ({ ...f, url: e.target.value }))
                  }
                  placeholder="https://…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
                  data-testid="manual-url"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  We&apos;ll try to auto-extract title & description. You can override below.
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                  Title (optional)
                </label>
                <input
                  type="text"
                  value={manualForm.title}
                  onChange={(e) =>
                    setManualForm((f) => ({ ...f, title: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
                  data-testid="manual-title"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                  Snippet (optional)
                </label>
                <textarea
                  rows={2}
                  value={manualForm.snippet}
                  onChange={(e) =>
                    setManualForm((f) => ({ ...f, snippet: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none resize-none"
                  data-testid="manual-snippet"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                  Source name (optional)
                </label>
                <input
                  type="text"
                  value={manualForm.source}
                  onChange={(e) =>
                    setManualForm((f) => ({ ...f, source: e.target.value }))
                  }
                  placeholder="e.g. Times of India"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
                  data-testid="manual-source"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setManualOpen(false)}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-[#FF9933] hover:bg-[#E67300] text-white rounded-md font-medium"
                  data-testid="manual-submit"
                >
                  Add &amp; Publish
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
